/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return */

import { polyfill } from 'es6-promise';
import _ from 'lodash';
import { push } from 'react-router-redux';
import { startAction, completeAction, handleError, reportError, reportSuccess } from './common';
import { accountServiceClient } from '../utils/api';

polyfill();

export const actionTypes = {
  ORDER_FETCH_ALL_BY_ACCOUNT_SUCCESS: 'ORDER_FETCH_ALL_BY_ACCOUNT_SUCCESS',
  GET_ACCOUNT_BY_ID_SUCCESS: 'GET_ACCOUNT_BY_ID_SUCCESS',
  CREATE_SUCCESS: 'ACCOUNT_CREATE_SUCCESS',
  COMPANY_ACCOUNTS_FETCH_SUCCESS: 'COMPANY_ACCOUNTS_FETCH_SUCCESS',
  JOBS_FETCH_SUCCESS: 'JOBS_FETCH_SUCCESS',
  BATCH_FETCH_SUCCESS: 'BATCH_FETCH_SUCCESS',
  INVITE_NEW_USER: 'INVITE_NEW_USER',
  RECEIVE_FILESETS: 'RECEIVE_FILESETS',
  CHANGE_PAGE: 'CHANGE_PAGE',
  FILESET_ACTION_SUCCESS: 'FILESET_ACTION_SUCCESS',
  DELETE_FILESET_SUCCESS: 'DELETE_FILESET_SUCCESS',
  LAYOUT_UPLOAD: 'LAYOUT_UPLOAD',
  RETRIEVE_CREDENTIALS_SUCCESS: 'RETRIEVE_CREDENTIALS_SUCCESS',
  RETRIVE_RECORD_LEVELS_SUCCESS: 'RETRIVE_RECORD_LEVELS_SUCCESS',
  DATASET_FETCH_SUCCESS: 'DATASET_FETCH_SUCCESS',
  UPDATE_DATASET_SUCCESS: 'UPDATE_DATASET_SUCCESS',
  RECEIVE_PLATFORMS: 'RECEIVE_PLATFORMS',
  CREATE_CONFIG_SUCCESS: 'CREATE_CONFIG_SUCCESS',
  RECEIVE_CONFIG_SETTINGS: 'RECEIVE_CONFIG_SETTINGS',
  UPDATE_CONFIG_SUCCESS: 'UPDATE_CONFIG_SUCCESS',
  DELETE_CONFIG_SUCCESS: 'DELETE_CONFIG_SUCCESS',
  RECEIVE_FIELDS: 'RECEIVE_FIELDS',
  OPEN_FILESET_PAGE: 'OPEN_FILESET_PAGE',
  VIEW_USER_TYPES: 'VIEW_USER_TYPES',
  SET_SELECTED_ACCOUNT_SUCCESS: 'SET_SELECTED_ACCOUNT_SUCCESS',
  RECEIVE_ACCOUNT_CC_EMAILS_SUCCESS: 'RECEIVE_ACCOUNT_CC_EMAILS_SUCCESS',
};

// Connect to the API
const swaggerApi = accountServiceClient();

const getAccountSuccess = currAccount => ({
  type: actionTypes.GET_ACCOUNT_BY_ID_SUCCESS,
  currAccount,
});

// Get info for a single Account
export function getAccountById(accountId, token) {
  const actionText = 'Retrieving Account By Id';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .get({
          account_id: accountId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const accountObj = response.obj;
            dispatch(getAccountSuccess(accountObj));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const viewUsersInfo = viewOnlyAccount => ({
  type: actionTypes.VIEW_USER_TYPES,
  viewOnlyAccount,
});

export function viewUsers(account) {
  const actionText = 'Redirecting Admin to User Page';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(viewUsersInfo(account));
    dispatch(push(`/users/${account.id}`));
    completeAction(dispatch, actionText);
  };
}

// pass accountID so it can be used to invite a new user
const inviteNewUser = id => ({
  type: actionTypes.INVITE_NEW_USER,
  id,
});

export function openInviteNewUser(id) {
  const actionText = 'Redirecting to User Invite';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(inviteNewUser(id));
    completeAction(dispatch, actionText);
  };
}

// dispatches an action when credentials have
// been successfully received/updated
const retrieveCredentialsSuccess = sftpInfo => ({
  type: actionTypes.RETRIEVE_CREDENTIALS_SUCCESS,
  sftpInfo,
});

// updates SFTP credentials associated with an account
export function updateCredentials(accountId, updateInfo, token) {
  const actionText = 'Update Credentials';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .updateFTPCredentials({
          Authorization: `bearer ${token}`,
          account_id: accountId,
          body: {
            username: updateInfo.username,
            passwordHash: updateInfo.passwordHash,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, 'Your password has been updated!', response.obj);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const { message } = statusText;
          if (err.status === 404) {
            const failedInfo = undefined;
            dispatch(retrieveCredentialsSuccess(failedInfo));
            completeAction(dispatch, actionText);
          } else {
            const error = new Error(message);
            reportError(dispatch, error);
          }
        });
    });
  };
}

// retrieves SFTP Credentials associated with an account
export function retrieveCredentials(accountId, token) {
  const actionText = 'Retrieving Credentials';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .listFTPCredentials({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const sftpInfo = response.obj;
            dispatch(retrieveCredentialsSuccess(sftpInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const { message } = statusText;
          if (err.status === 404) {
            const failedInfo = undefined;
            dispatch(retrieveCredentialsSuccess(failedInfo));
            completeAction(dispatch, actionText);
          } else {
            const error = new Error(message);
            reportError(dispatch, error);
          }
        });
    });
  };
}

// Removes SFTP Credentials associated with an account
export function removeCredentials(accountId, token) {
  const actionText = 'Removing Credentials';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .deleteFTPCredentials({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          // If everything's cool, show success message
          if (response.status >= 200 && response.status < 300) {
            dispatch(push('/superadmin/accounts'));
            reportSuccess(dispatch, 'SFTP Credentials have been Removed!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// creates SFTP Credentials associated with an account
export function createCredentials(accountId, credentialsInfo, token) {
  const actionText = 'Creating SFTP Credentials';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .createFTPCredentials({
          Authorization: `bearer ${token}`,
          account_id: accountId,
          body: {
            accountId,
            username: _.trim(credentialsInfo.username),
            passwordHash: credentialsInfo.passwordHash,
          },
        })
        .then(response => {
          // If everything's cool, dispatch success
          if (response.status >= 200 && response.status < 300) {
            dispatch(push('/superadmin/accounts'));
            reportSuccess(dispatch, 'SFTP Credentials have been Created!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          let { message } = statusText;
          if (err.status === 409) {
            message = 'Error: Username already in use. Please select a different username.';
          }
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}
// Create a company account
export function createNewAccount(token, accountData) {
  const actionText = 'Creating Company Account';
  const status = accountData.status || 1;

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .createAccount({
          Authorization: `bearer ${token}`,
          body: {
            companyName: accountData.companyName,
            status,
            creditsEnabled: accountData.creditsEnabled,
            accountTypeId: accountData.accountTypeId,
            address1: accountData.address1,
            address2: accountData.address2 || ' ',
            city: accountData.city,
            stateProvince: accountData.stateProvince,
            postcode: accountData.postcode,
            country: accountData.country,
            url: accountData.url,
            portalHost: accountData.portalHost,
            phone: accountData.phone,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            dispatch(push('/superadmin/accounts'));
            reportSuccess(dispatch, 'New Company Account Created!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const error = new Error(statusText.message);
          reportError(dispatch, error);
        });
    });
  };
}

// Update an Account!
export function updateAccount(token, accountData, accountId) {
  const actionText = 'Updating an Account';

  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .updateAccount({
          account_id: accountId,
          Authorization: `bearer ${token}`,
          body: accountData,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            dispatch(push('/superadmin/accounts'));
            reportSuccess(dispatch, 'Account Edit Successful!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Create Account List Action
const receiveAccounts = accounts => ({
  type: actionTypes.COMPANY_ACCOUNTS_FETCH_SUCCESS,
  accounts,
});

// List Existing Company Accounts
export function listAccounts(token, resolve) {
  const actionText = 'Listing Company Accounts';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .listAccounts({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const accountsInfo = response.obj;
            dispatch(receiveAccounts(accountsInfo));
            completeAction(dispatch, actionText);
            if (resolve) resolve();
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Create Job List Action
const receiveJobs = jobs => ({
  type: actionTypes.JOBS_FETCH_SUCCESS,
  jobs,
});

export function listJobs(token) {
  const actionText = 'Listing all jobs';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.job
        .getAllJobs({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const jobsInfo = response.obj;
            dispatch(receiveJobs(jobsInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveBatches = batches => ({
  type: actionTypes.BATCH_FETCH_SUCCESS,
  batches,
});

export function listBatches(token, jobId) {
  const actionText = 'Listing all batches for job';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.job
        .getAllBatches({
          Authorization: `bearer ${token}`,
          job_id: jobId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const batchesInfo = response.obj;
            dispatch(receiveBatches(batchesInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveDataset = dataset => ({
  type: actionTypes.DATASET_FETCH_SUCCESS,
  dataset,
});

export function getDatasetInfo(token, id) {
  const actionText = 'Retrieving Dataset Information';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .getDataSet({
          Authorization: `bearer ${token}`,
          dataset_id: id,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const datasetInfo = response.obj;
            dispatch(receiveDataset(datasetInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const updateDatasetSuccess = dataset => ({
  type: actionTypes.UPDATE_DATASET_SUCCESS,
  dataset,
});

export function updateDataset(token, datasetInfo, datasetId) {
  const actionText = 'Updating Dataset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .updateDataSet({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          body: datasetInfo,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const dataset = response.obj;
            dispatch(updateDatasetSuccess(dataset));
            reportSuccess(dispatch, 'List Updated', null);
            completeAction(dispatch, actionText);
            const nextPage = `/data/dataset/${datasetId}`;
            dispatch(push(nextPage));
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function cancelForm(nextPage) {
  return dispatch => {
    dispatch(push(nextPage));
  };
}

// List filesets for a dataset
const receiveFilesets = filesets => ({
  type: actionTypes.RECEIVE_FILESETS,
  filesets,
});

export function listFilesets(token, datasetId) {
  const actionText = 'Fetching filesets';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .getFileSets({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const filesetsInfo = response.obj;
            dispatch(receiveFilesets(filesetsInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const nextPageSuccess = (nextPage, values) => ({
  type: actionTypes.CHANGE_PAGE,
  nextPage,
  values,
});

export function changePage(nextPage, values) {
  const actionText = 'Progressing to next page';

  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(nextPageSuccess(nextPage, values));
    completeAction(dispatch, actionText);
  };
}

const createOrUpdateFileset = fileset => ({
  type: actionTypes.FILESET_ACTION_SUCCESS,
  fileset,
});

export function createFileset(token, fileset, datasetId) {
  const actionText = 'Creating New Fileset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .addFileSet({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          body: fileset,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const filesetInfo = response.obj;
            dispatch(createOrUpdateFileset(filesetInfo));
            reportSuccess(dispatch, 'New Fileset Created!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updateFileset(token, fileset, datasetId, filesetId) {
  const actionText = 'Updating Fileset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .updateFileSet({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          fileset_id: filesetId,
          body: {
            dataFilenamePattern: fileset.dataFilenamePattern,
            layoutFilename: fileset.layoutFilename,
            recordLevelTypeId: fileset.recordLevelTypeId,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const filesetInfo = response.obj;
            dispatch(createOrUpdateFileset(filesetInfo));
            reportSuccess(dispatch, 'Fileset Updated!', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const deleteFilesetSuccess = fileset => ({
  type: actionTypes.DELETE_FILESET_SUCCESS,
  fileset,
});

export function deleteFileset(token, datasetId, fileset) {
  const actionText = 'Deleteing fileset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .deleteFileSet({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          fileset_id: fileset.id,
          body: {
            dataFilenamePattern: fileset.dataFilenamePattern,
            layoutFilename: fileset.layoutFilename,
            recordLevelTypeId: fileset.recordLevelTypeId,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const filesetInfo = response.obj;
            dispatch(deleteFilesetSuccess(filesetInfo));
            reportSuccess(dispatch, 'Fileset Deleted');
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const createLayoutSuccess = layoutInfo => ({
  type: actionTypes.LAYOUT_UPLOAD,
  layoutInfo,
});

export function createLayout(token, layoutData, resolve) {
  const actionText = 'Uploading layout to fileset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      const upload = {
        Authorization: `bearer ${token}`,
        dataset_id: layoutData.datasetId,
        fileset_id: layoutData.filesetId,
        file: layoutData.file,
      };
      if (layoutData.codes && layoutData.codes !== undefined) {
        upload.codes = layoutData.codes;
      }
      client.datasets
        .fileSetLayoutUpload(upload)
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const LayoutInfo = response.obj;
            dispatch(createLayoutSuccess(LayoutInfo));
            reportSuccess(dispatch, 'Layout Upload Successful');
            resolve();
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveFields = (fields, filesetId) => ({
  type: actionTypes.RECEIVE_FIELDS,
  fields,
  filesetId,
});

export function fetchFieldsById(token, datasetId, filesetId) {
  const actionText = 'fetching fields for fileset';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .getFields({
          Authorization: `bearer ${token}`,
          dataset_id: datasetId,
          fileset_id: filesetId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const fields = response.obj;
            dispatch(receiveFields(fields, filesetId));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receivePlatforms = platforms => ({
  type: actionTypes.RECEIVE_PLATFORMS,
  platforms,
});

export function fetchPlatforms() {
  const actionText = 'Fetching platforms for configuration';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.config
        .listPlatforms({})
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const platforms = response.obj;

            dispatch(receivePlatforms(platforms));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveConfigSettings = configSettings => ({
  type: actionTypes.RECEIVE_CONFIG_SETTINGS,
  configSettings,
});

export function fetchConfigSettings(token, datasetId, platformCode) {
  const actionText = 'Getting configuration for package';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .getPlatformConfig({
          Authorization: `bearer ${token}`,
          platform_code: platformCode,
          dataset_id: datasetId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const configInfo = response.obj;
            dispatch(receiveConfigSettings(configInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const { message } = statusText;
          if (err.status === 404) {
            const failedInfo = undefined;
            dispatch(receiveConfigSettings(failedInfo));
            completeAction(dispatch, actionText);
          } else {
            const error = new Error(message);
            reportError(dispatch, error);
          }
        });
    });
  };
}

const createConfigSettingsSuccess = configSettings => ({
  type: actionTypes.CREATE_CONFIG_SUCCESS,
  configSettings,
});

export function createConfigSettings(token, platformCode, datasetId, body) {
  const actionText = 'Creating configuration for package';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .addPlatformConfig({
          Authorization: `bearer ${token}`,
          platform_code: platformCode,
          dataset_id: datasetId,
          body,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const configSettings = response.obj;
            dispatch(createConfigSettingsSuccess(configSettings));
            completeAction(dispatch, actionText);
            // dispatch(push(`/data/dataset/${datasetId}`));
            reportSuccess(dispatch, 'Configuration Settings Successfully Created');
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const updateConfigSettingsSuccess = configSettings => ({
  type: actionTypes.UPDATE_CONFIG_SUCCESS,
  configSettings,
});

export function updateConfigSettings(token, platformCode, datasetId, body) {
  const actionText = 'Updating configuration for package';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .updatePlatformConfig({
          Authorization: `bearer ${token}`,
          platform_code: platformCode,
          dataset_id: datasetId,
          body,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const configInfo = response.obj;
            dispatch(updateConfigSettingsSuccess(configInfo));
            completeAction(dispatch, actionText);
            reportSuccess(dispatch, 'Configuration Settings Successfully Updated');
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const deleteConfigSettingsSuccess = configSettings => ({
  type: actionTypes.DELETE_CONFIG_SUCCESS,
  configSettings,
});

export function deleteConfigSettings(token, platformCode, datasetId) {
  const actionText = 'Deleting configuration for package';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.datasets
        .deletePlatformConfig({
          Authorization: `bearer ${token}`,
          platform_code: platformCode,
          dataset_id: datasetId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const configInfo = response.obj;
            dispatch(deleteConfigSettingsSuccess(configInfo));
            completeAction(dispatch, actionText);
            dispatch(push(`/data/dataset/${datasetId}`));
            reportSuccess(dispatch, 'Configuration Settings Successfully Deleted');
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const openFilesetPageSuccess = (datasetId, datasetName) => ({
  type: actionTypes.OPEN_FILESET_PAGE,
  datasetId,
  datasetName,
});

export function openFilesetPage(datasetId, datasetName) {
  const actionText = 'passing info to open the fileset page';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(openFilesetPageSuccess(datasetId, datasetName));
    completeAction(dispatch, actionText);
    dispatch(push(`/superadmin/filesets/${datasetId}`));
  };
}

const retrieveRecordLevelTypesSuccess = recordLevels => ({
  type: actionTypes.RETRIVE_RECORD_LEVELS_SUCCESS,
  recordLevels,
});

export function retrieveRecordLevelTypes() {
  const actionText = 'Retrieving record level types';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.config
        .listRecordLevelTypes()
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const recordLevelTypes = response.obj;
            dispatch(retrieveRecordLevelTypesSuccess(recordLevelTypes));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveAllAccountOrdersSuccess = orders => ({
  type: actionTypes.ORDER_FETCH_ALL_BY_ACCOUNT_SUCCESS,
  orders,
});

export function adminListOrders(accountId, token) {
  const actionText = 'Getting orders for all accounts';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .getOrdersForAccount({
          accountId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const orders = response.obj;
            dispatch(receiveAllAccountOrdersSuccess(orders));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const error = new Error(statusText.message);
          reportError(dispatch, error);
        });
    });
  };
}

const setSelectedAccountSuccess = account => ({
  type: actionTypes.SET_SELECTED_ACCOUNT_SUCCESS,
  account,
});

export function setSelectedAccount(account) {
  const actionText = 'Setting selected account';
  return dispatch => {
    dispatch(setSelectedAccountSuccess(account));
    completeAction(dispatch, actionText);
  };
}

const receiveAccountCCEmailsSuccess = accountEmails => ({
  type: actionTypes.RECEIVE_ACCOUNT_CC_EMAILS_SUCCESS,
  accountEmails,
});

export function getAccountCCEmails(accountId, token) {
  const actionText = 'Getting all account cc emails';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .getAccountEmails({
          account_id: accountId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const accountEmails = response.obj;
            dispatch(receiveAccountCCEmailsSuccess(accountEmails));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function createAccountCCEmail(accountId, email, token) {
  const actionText = 'Adding email to account cc email list';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .createAccountEmail({
          account_id: accountId,
          email,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const accountEmails = response.obj;
            dispatch(receiveAccountCCEmailsSuccess(accountEmails));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function removeAccountCCEmail(accountId, emailId, token) {
  const actionText = 'Removing email from account cc email list';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .removeAccountEmail({
          account_id: accountId,
          email_id: emailId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const accountEmails = response.obj;
            dispatch(receiveAccountCCEmailsSuccess(accountEmails));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}
